<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <v-dialog
    id="add-user-role-modal"
    v-model="display"
    class="modal-wrapper"
    width="1000"
    dark
    @click:outside="handleCancel"
  >
    <v-card class="modal">
      <v-card-title class="pa-4 pl-11">
        <div id="add-user-role-title">{{ $t('access.user_role.buttons.add_user_role') }}</div>
        <v-icon id="add-user-role-close-btn" class="close" @click="handleCancel">mdi-close</v-icon>
      </v-card-title>
      <v-divider />
      <v-card-text class="content py-6 px-12">
        <v-col>
          <v-row class="mb-1"><div class="text">{{ $t('access.user_role.name') }}</div></v-row>
          <v-row>
            <v-text-field
              id="user-role-name"
              v-model="displayName"
              hide-details
              outlined
              dark
              dense
              :class="`${getError !== undefined ? 'on-error' : ''}`"
            />
          </v-row>
          <v-row class="error-bloc">
            <p v-if="getError !== undefined" class="error-message">{{ $t(getTranslatedError) }}</p>
          </v-row>
          <v-row class="text">{{ $t('access.user_role.permissions.title') }}</v-row>
        </v-col>
        <div class="permissions-wrapper">
          <PermissionsList
            class="mt-1 pa-0"
            :permissions="getPermissionsFeatures"
            :role="role"
            :edition="true"
            @draftUpdated="handleUpdate($event)"
          />
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4 pl-11">
        <v-btn
          id="add-user-role-add-btn"
          class="ctp-btn my-0"
          text
          dark
          @click="handleAdd"
        >
          {{ $t('access.user_role.buttons.add') }}
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import useStore from '@/utils/store/common';
import PermissionsList from '@/views/UserRole/components/Right/components/PermissionsList/PermissionsList.vue';
import { computed, ref, watch } from 'vue';

const props = defineProps({
  display: {
    type: Boolean,
    default: false
  }
});

const display = computed(() => props.display);

const emit = defineEmits(['cancel']);

const store = useStore();

const getPermissionsFeatures = computed(() => store.getters['userRoles/getPermissionsFeatures']);

const getError = computed(() => store.getters['userRoles/getError']);

const getTranslatedError = computed(() => {
  const err = getError.value;
  if (err === 'This user role already exists, please enter a different role name.') {
    return 'access.user_role.error.duplicate';
  }
  if (err === 'This user role name contains forbidden characters, please use only letters, digits, dot or dash.') {
    return 'access.user_role.error.forbidden';
  }
  return 'device.list.item.unknow';
});

let role: {displayName: string, permissions: string[]} = {
  displayName: '',
  permissions: []
};

const displayName = ref('');

const permissions = ref([] as string[]);

function reset() {
  displayName.value = '';
  permissions.value = [];
  role = {
    displayName: '',
    permissions: []
  };
}

function handleAdd() {
  const data = {
    displayName: displayName.value,
    permissions: permissions.value
  };

  store.dispatch('userRoles/addUserRoleWithPermissions', data);
  reset();
}

function handleCancel() {
  reset();
  emit('cancel', false);

  store.dispatch('userRoles/setError', undefined);
}

function handleUpdate(event: string[]) {
  permissions.value = event;
}

watch(
  () => displayName,
  () => {
    store.dispatch('userRoles/setError', undefined);
  },
  {
    immediate: true
  }
);

</script>
<style lang="scss" scoped>
@import './styles/modal.scss';
</style>
